import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import InstallPWA from '@/components/InstallPWA';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  adminGetBookings,
  adminCancelBooking,
  adminApproveBooking,
  adminDenyBooking,
  adminGetBlockedSlots,
  adminBlockSlot,
  adminBlockDateRange,
  adminGetSettings,
  adminUpdateBookingsSettings,
  adminListAfterHoursEnquiries,
  adminUnblockSlot,
  adminGetUsers,
  adminCreateUser,
  adminDeleteUser,
  adminChangePassword,
  adminGetHomeContent,
  adminGetVisitorStats,
} from '@/lib/api';
import {
  Settings,
  LogOut,
  Calendar as CalendarIcon,
  Users,
  Lock,
  X,
  Plus,
  Trash2,
  Ban,
  CalendarRange,
  Wrench,
  Power,
  Phone,
  Mail,
  Car,
  Home,
  Eye,
  Bot,
  Edit,
  Moon,
} from 'lucide-react';
import HomeContentEditor from '@/components/HomeContentEditor';
import BlockCalendar from '@/components/BlockCalendar';
import PushSubscribe from '@/components/PushSubscribe';
import GoogleCalendarConnect from '@/components/GoogleCalendarConnect';
import WorkshopSchedule from '@/components/WorkshopSchedule';
import ChatbotManager from '@/components/ChatbotManager';
import EditBookingDialog from '@/components/EditBookingDialog';
import MetaTokenBanner from '@/components/MetaTokenBanner';
import AfterHoursEnquiries from '@/components/AfterHoursEnquiries';

const WEEKDAY_SLOTS = [
  { id: 'slot_m1', label: '8:00 AM - 11:00 AM' },
  { id: 'slot_m2', label: '11:00 AM - 1:00 PM' },
  { id: 'slot_m3', label: '1:30 PM - 5:00 PM' },
];

// Legacy slot labels retained so admin can still display historical bookings
const LEGACY_SLOTS = [
  { id: 'slot_fs1', label: '8:30 AM - 12:30 PM' },
  { id: 'slot_fs2', label: '1:00 PM - 4:00 PM' },
  { id: 'slot_fs3', label: '5:00 PM - 8:00 PM' },
  { id: 'slot_wt1', label: '6:30 PM - 9:30 PM (Quieter Cars)' },
];

const ALL_TIME_SLOTS = [...WEEKDAY_SLOTS, ...LEGACY_SLOTS];

const DEFAULT_HOME_CONTENT = {
  hero_title: 'NAKI TUNE 2.0',
  hero_subtitle: 'Professional Dyno Tuning Services',
  hero_description: 'Unlock your vehicle\'s true potential with precision dyno tuning.',
  about_title: 'What We Do',
  about_description: 'NAKI TUNE 2.0 offers professional dynamometer tuning services.',
  services: [
    { title: 'Power Runs', description: 'Accurate horsepower and torque measurements', icon: 'gauge' },
    { title: 'ECU Tuning', description: 'Custom maps for maximum performance', icon: 'zap' },
    { title: 'Diagnostics', description: 'Comprehensive vehicle health checks', icon: 'wrench' }
  ],
  gallery_images: [],
  contact_phone: '0278150446',
  contact_email: 'Nakitune@outlook.co.nz',
  contact_name: 'William',
  tiktok_profile_url: 'https://www.tiktok.com/@nakituneperformance',
  tiktok_video_urls: []
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [homeContent, setHomeContent] = useState(DEFAULT_HOME_CONTENT);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [visitorStats, setVisitorStats] = useState({ total: 0, today: 0 });
  const [afterHoursNewCount, setAfterHoursNewCount] = useState(0);
  
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockDate, setBlockDate] = useState(null);
  const [blockSlotId, setBlockSlotId] = useState('full_day');
  const [blockReason, setBlockReason] = useState('');

  const [showBlockRangeDialog, setShowBlockRangeDialog] = useState(false);
  const [blockRangeStart, setBlockRangeStart] = useState(null);
  const [blockRangeEnd, setBlockRangeEnd] = useState(null);
  const [blockRangeReason, setBlockRangeReason] = useState('');
  const [blockRangeSaving, setBlockRangeSaving] = useState(false);

  const [bookingsEnabled, setBookingsEnabled] = useState(true);
  const [closedMessage, setClosedMessage] = useState('');
  const [savingBookingsToggle, setSavingBookingsToggle] = useState(false);
  
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordChange, setNewPasswordChange] = useState('');

  // Edit booking dialog (used for GCal-imported bookings mainly)
  const [editingBooking, setEditingBooking] = useState(null);
  const openEditBooking = (b) => setEditingBooking(b);
  const handleBookingSaved = () => fetchData();

  const adminUsername = localStorage.getItem('adminUsername');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsRes, blockedRes, usersRes, contentRes, statsRes, settingsRes, enquiriesRes] = await Promise.all([
        adminGetBookings(),
        adminGetBlockedSlots(),
        adminGetUsers(),
        adminGetHomeContent(),
        adminGetVisitorStats().catch(() => ({ data: { total: 0, today: 0 } })),
        adminGetSettings().catch(() => ({ data: { bookings_enabled: true, closed_message: '' } })),
        adminListAfterHoursEnquiries().catch(() => ({ data: [] })),
      ]);
      setBookings(bookingsRes.data);
      setBlockedSlots(blockedRes.data);
      setAdminUsers(usersRes.data);
      if (contentRes.data) setHomeContent(contentRes.data);
      if (statsRes.data) setVisitorStats(statsRes.data);
      if (settingsRes.data) {
        setBookingsEnabled(settingsRes.data.bookings_enabled !== false);
        setClosedMessage(settingsRes.data.closed_message || '');
      }
      // Keep the After-Hours tab badge up to date without needing the user to open the tab.
      setAfterHoursNewCount((enquiriesRes.data || []).filter((e) => e.status === 'new').length);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  // Poll for new pending bookings every 30 seconds (auto-refresh)
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('adminToken');
      if (token) fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Tab title badge — reflect pending count in browser tab
  useEffect(() => {
    const pending = bookings.filter((b) => b.status === 'pending').length;
    const base = 'NAKI TUNE 2.0 | Admin';
    document.title = pending > 0 ? `(${pending}) ${base}` : base;
    return () => {
      document.title = 'NAKI TUNE 2.0 | Dyno Tuning';
    };
  }, [bookings]);

  // Handle ?approve=<id> and ?pendingId=<id> deep links from push notifications
  useEffect(() => {
    if (initialLoad) return;
    const params = new URLSearchParams(window.location.search);
    const approveId = params.get('approve');
    const pendingId = params.get('pendingId');
    if (!approveId && !pendingId) return;

    if (approveId) {
      const b = bookings.find((x) => x.id === approveId);
      if (!b) {
        toast.error('Booking not found');
      } else if (b.status !== 'pending') {
        toast.info(`Booking already ${b.status}`);
      } else if (window.confirm(`Approve booking for ${b.customer?.name} on ${b.date} (${b.slot_label})?`)) {
        handleApproveBooking(approveId);
      }
    }
    setActiveTab('bookings');
    window.history.replaceState({}, '', '/admin/dashboard');
  }, [initialLoad, bookings.length]);

  // Listen for messages from the service worker (fallback for older browsers)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        navigate(event.data.url.replace(/^\/admin\/?/, '/admin/dashboard'));
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    navigate('/admin');
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await adminCancelBooking(bookingId);
      toast.success('Booking cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handleApproveBooking = async (bookingId) => {
    try {
      await adminApproveBooking(bookingId);
      toast.success('Booking approved — customer emailed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve booking');
    }
  };

  const handleDenyBooking = async (bookingId) => {
    const reason = window.prompt('Reason for denying this booking (optional — customer will see it):');
    if (reason === null) return; // user cancelled prompt
    try {
      await adminDenyBooking(bookingId, reason);
      toast.success('Booking denied — customer emailed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny booking');
    }
  };

  const handleBlockSlot = async () => {
    if (!blockDate) {
      toast.error('Please select a date');
      return;
    }
    try {
      await adminBlockSlot({
        date: format(blockDate, 'yyyy-MM-dd'),
        slot_id: blockSlotId === 'full_day' ? null : blockSlotId,
        reason: blockReason || null,
      });
      toast.success('Slot blocked successfully');
      setShowBlockDialog(false);
      setBlockDate(null);
      setBlockSlotId('full_day');
      setBlockReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block slot');
    }
  };

  const handleUnblockSlot = async (blockedId) => {
    try {
      await adminUnblockSlot(blockedId);
      toast.success('Slot unblocked');
      fetchData();
    } catch (error) {
      toast.error('Failed to unblock slot');
    }
  };

  const handleBlockDateRange = async () => {
    if (!blockRangeStart || !blockRangeEnd) {
      toast.error('Please pick both start and end dates');
      return;
    }
    if (blockRangeEnd < blockRangeStart) {
      toast.error('End date must be on or after start date');
      return;
    }
    setBlockRangeSaving(true);
    try {
      const res = await adminBlockDateRange({
        start_date: format(blockRangeStart, 'yyyy-MM-dd'),
        end_date: format(blockRangeEnd, 'yyyy-MM-dd'),
        reason: blockRangeReason || null,
      });
      toast.success(res.data.message || 'Dates blocked');
      setShowBlockRangeDialog(false);
      setBlockRangeStart(null);
      setBlockRangeEnd(null);
      setBlockRangeReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block date range');
    } finally {
      setBlockRangeSaving(false);
    }
  };

  const handleToggleBookings = async (nextEnabled) => {
    setSavingBookingsToggle(true);
    // Optimistic UI
    const prev = bookingsEnabled;
    setBookingsEnabled(nextEnabled);
    try {
      await adminUpdateBookingsSettings({ enabled: nextEnabled, closed_message: closedMessage });
      toast.success(nextEnabled ? 'Bookings are now OPEN' : 'Bookings are now CLOSED');
    } catch (error) {
      setBookingsEnabled(prev); // revert
      toast.error('Failed to update booking status');
    } finally {
      setSavingBookingsToggle(false);
    }
  };

  const handleSaveClosedMessage = async () => {
    setSavingBookingsToggle(true);
    try {
      await adminUpdateBookingsSettings({ enabled: bookingsEnabled, closed_message: closedMessage });
      toast.success('Message saved');
    } catch (error) {
      toast.error('Failed to save message');
    } finally {
      setSavingBookingsToggle(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUsername || !newPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await adminCreateUser({ username: newUsername, password: newPassword });
      toast.success(`User '${newUsername}' created`);
      setShowAddUserDialog(false);
      setNewUsername('');
      setNewPassword('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Delete user '${username}'?`)) return;
    try {
      await adminDeleteUser(username);
      toast.success(`User '${username}' deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPasswordChange) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await adminChangePassword({
        current_password: currentPassword,
        new_password: newPasswordChange,
      });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPasswordChange('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const pendingBookings = bookings.filter((b) => b.status === 'pending');
  const confirmedBookings = bookings.filter((b) => b.status === 'confirmed');
  const activeBookings = bookings.filter((b) => b.status !== 'cancelled' && b.status !== 'denied');
  const cancelledBookings = bookings.filter((b) => b.status === 'cancelled' || b.status === 'denied');

  if (initialLoad) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-50 backdrop-blur-lg">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-600 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="brand-title text-xl uppercase tracking-wide text-white">
                NAKI TUNE 2.0
              </h1>
              <p className="text-xs text-zinc-500">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-3">
              <PushSubscribe testIdSuffix="desktop" />
              <GoogleCalendarConnect />
            </div>
            <span className="text-zinc-400 text-sm hidden sm:block">
              Logged in as <span className="text-white font-medium">{adminUsername}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
        <div className="md:hidden flex flex-col gap-2 items-center pb-3 -mt-1">
          <PushSubscribe testIdSuffix="mobile" />
          <GoogleCalendarConnect />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* Meta token health (hidden when token is OK) */}
        <MetaTokenBanner />

        {/* Visitor Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="glass p-4 border border-zinc-800 bg-zinc-900/50 text-center" data-testid="stat-total-visits">
            <Eye className="w-5 h-5 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">{visitorStats.total}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Visits</p>
          </div>
          <div className="glass p-4 border border-zinc-800 bg-zinc-900/50 text-center" data-testid="stat-today-visits">
            <Eye className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">{visitorStats.today}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Today</p>
          </div>
          <div className="glass p-4 border border-zinc-800 bg-zinc-900/50 text-center" data-testid="stat-active-bookings">
            <CalendarIcon className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
            <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">{activeBookings.length}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Active Bookings</p>
          </div>
        </div>

        {/* Bookings Master Toggle */}
        <div
          className={`glass p-4 border mb-6 transition-colors ${
            bookingsEnabled
              ? 'border-green-800/50 bg-green-900/10'
              : 'border-red-800/60 bg-red-900/20'
          }`}
          data-testid="bookings-master-toggle"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-sm ${
                  bookingsEnabled ? 'bg-green-600/30 text-green-500' : 'bg-red-600/30 text-red-400'
                }`}
              >
                <Power className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide">
                  Bookings {bookingsEnabled ? 'Open' : 'Closed'}
                </p>
                <p className="text-zinc-400 text-xs">
                  {bookingsEnabled
                    ? 'Customers can book any available slot.'
                    : 'All slots are hidden from customers. Turn back on when ready.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider text-zinc-500">
                {bookingsEnabled ? 'ON' : 'OFF'}
              </span>
              <Switch
                checked={bookingsEnabled}
                onCheckedChange={handleToggleBookings}
                disabled={savingBookingsToggle}
                data-testid="bookings-toggle-switch"
                className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-red-700"
              />
            </div>
          </div>
          {!bookingsEnabled && (
            <div className="mt-4 pt-4 border-t border-red-800/40 space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">
                Message shown to customers (optional)
              </Label>
              <Textarea
                value={closedMessage}
                onChange={(e) => setClosedMessage(e.target.value)}
                onBlur={handleSaveClosedMessage}
                placeholder="e.g., Closed for dyno maintenance until Mon 15th. Sorry for the inconvenience!"
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
                rows={2}
                data-testid="closed-message-input"
              />
              <p className="text-zinc-500 text-xs">Auto-saves when you click outside the box.</p>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex w-full overflow-x-auto justify-start md:justify-center gap-1">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-green-600" data-testid="tab-bookings">
              <CalendarIcon className="w-4 h-4 mr-2" />Bookings ({activeBookings.length})
            </TabsTrigger>
            <TabsTrigger value="blocked" className="data-[state=active]:bg-green-600" data-testid="tab-blocked">
              <Ban className="w-4 h-4 mr-2" />Blocked ({blockedSlots.length})
            </TabsTrigger>
            <TabsTrigger value="workshop" className="data-[state=active]:bg-green-600" data-testid="tab-workshop">
              <Wrench className="w-4 h-4 mr-2" />Workshop
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-green-600" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />Users ({adminUsers.length})
            </TabsTrigger>
            <TabsTrigger value="home" className="data-[state=active]:bg-green-600" data-testid="tab-home">
              <Home className="w-4 h-4 mr-2" />Home Page
            </TabsTrigger>
            <TabsTrigger value="chatbot" className="data-[state=active]:bg-green-600" data-testid="tab-chatbot">
              <Bot className="w-4 h-4 mr-2" />Chatbot
            </TabsTrigger>
            <TabsTrigger value="afterhours" className="data-[state=active]:bg-green-600" data-testid="tab-afterhours">
              <Moon className="w-4 h-4 mr-2" />After-Hours{afterHoursNewCount > 0 ? ` (${afterHoursNewCount})` : ''}
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {/* Pending Approval Section */}
            {pendingBookings.length > 0 && (
              <div className="space-y-3" data-testid="pending-section">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-yellow-400">
                    ⏳ Awaiting Your Approval
                  </h2>
                  <span className="px-2 py-1 bg-yellow-900/40 border border-yellow-700 text-yellow-300 text-xs uppercase tracking-wider">
                    {pendingBookings.length} pending
                  </span>
                </div>
                <p className="text-zinc-500 text-sm">Customers will only get their confirmation email after you approve.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pendingBookings.map((booking) => (
                    <BookingCard
                      key={booking.id}
                      booking={booking}
                      onCancel={handleCancelBooking}
                      onApprove={handleApproveBooking}
                      onDeny={handleDenyBooking}
                      onEdit={openEditBooking}
                    />
                  ))}
                </div>
              </div>
            )}

            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white pt-4">
              Confirmed Bookings
            </h2>
            {confirmedBookings.length === 0 ? (
              <div className="glass p-12 border border-zinc-800 bg-zinc-900/50 text-center">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-500">No confirmed bookings yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {confirmedBookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onCancel={handleCancelBooking}
                    onApprove={handleApproveBooking}
                    onDeny={handleDenyBooking}
                    onEdit={openEditBooking}
                  />
                ))}
              </div>
            )}
            {cancelledBookings.length > 0 && (
              <div className="mt-8">
                <h3 className="text-zinc-500 uppercase tracking-wider text-sm mb-4">Cancelled / Denied ({cancelledBookings.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                  {cancelledBookings.map((booking) => (
                    <CancelledBookingCard key={booking.id} booking={booking} />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Blocked Slots Tab */}
          <TabsContent value="blocked" className="space-y-4">
            <BlockCalendar
              blockedSlots={blockedSlots}
              bookings={bookings}
              onChanged={fetchData}
            />

            <div className="flex justify-between items-center flex-wrap gap-2 pt-4">
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">Blocked List</h2>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowBlockRangeDialog(true)}
                  variant="outline"
                  className="border-green-700 text-green-500 hover:bg-green-900/30 hover:text-green-400"
                  data-testid="block-range-btn"
                >
                  <CalendarRange className="w-4 h-4 mr-2" />Block Days Off (Range)
                </Button>
                <Button onClick={() => setShowBlockDialog(true)} className="bg-green-600 hover:bg-green-700" data-testid="add-block-btn">
                  <Plus className="w-4 h-4 mr-2" />Block Individual Slot
                </Button>
              </div>
            </div>
            <p className="text-zinc-500 text-sm">
              Or use <span className="text-zinc-300">Block Days Off</span> to block a whole holiday period at once, or <span className="text-zinc-300">Block Individual Slot</span> to block just a morning or evening slot on a specific day.
            </p>
            {blockedSlots.length === 0 ? (
              <div className="glass p-12 border border-zinc-800 bg-zinc-900/50 text-center">
                <Ban className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-500">No blocked slots</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {blockedSlots.map((blocked) => (
                  <BlockedSlotCard key={blocked.id} blocked={blocked} onUnblock={handleUnblockSlot} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Workshop Schedule Tab */}
          <TabsContent value="workshop" className="space-y-4">
            <WorkshopSchedule />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">Admin Users</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPasswordDialog(true)} className="border-zinc-700" data-testid="change-password-btn">
                  <Lock className="w-4 h-4 mr-2" />Change Password
                </Button>
                <Button onClick={() => setShowAddUserDialog(true)} className="bg-green-600 hover:bg-green-700" data-testid="add-user-btn">
                  <Plus className="w-4 h-4 mr-2" />Add User
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adminUsers.map((user) => (
                <UserCard key={user.id} user={user} currentUser={adminUsername} onDelete={handleDeleteUser} />
              ))}
            </div>
          </TabsContent>

          {/* Home Page Tab */}
          <TabsContent value="home">
            <HomeContentEditor initialContent={homeContent} onSave={setHomeContent} />
          </TabsContent>

          {/* Chatbot Tab */}
          <TabsContent value="chatbot">
            <ChatbotManager />
          </TabsContent>

          {/* After-Hours Enquiries Tab */}
          <TabsContent value="afterhours" className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Moon className="w-5 h-5 text-green-500" />
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
                After-Hours Enquiries
              </h2>
              <p className="text-zinc-500 text-xs ml-auto">
                Customer requests for sessions outside Mon–Sat 8am–5pm.
              </p>
            </div>
            <AfterHoursEnquiries onCountChange={setAfterHoursNewCount} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Block Slot Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">Block a Slot</DialogTitle>
            <DialogDescription className="text-zinc-400">Block a specific time slot or an entire day</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Date</Label>
              <div className="flex justify-center border border-zinc-800 rounded-sm p-2">
                <Calendar mode="single" selected={blockDate} onSelect={setBlockDate} className="rounded-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Slot</Label>
              <Select value={blockSlotId} onValueChange={setBlockSlotId}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800">
                  <SelectValue placeholder="Select slot" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="full_day">Full Day</SelectItem>
                  {ALL_TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot.id} value={slot.id}>{slot.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Reason (Optional)</Label>
              <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono" placeholder="e.g., Holiday, Maintenance" />
            </div>
            <Button onClick={handleBlockSlot} className="w-full bg-green-600 hover:bg-green-700">Block Slot</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block Date Range Dialog */}
      <Dialog open={showBlockRangeDialog} onOpenChange={setShowBlockRangeDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">
              <CalendarRange className="w-5 h-5 inline-block mr-2 mb-1" />
              Block Multiple Days Off
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Sick, on holiday, or away? Pick your first and last day off — every day in between will be blocked from bookings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 text-xs uppercase tracking-wider">First day off</Label>
                <div className="flex justify-center border border-zinc-800 rounded-sm p-2">
                  <Calendar
                    mode="single"
                    selected={blockRangeStart}
                    onSelect={setBlockRangeStart}
                    disabled={{ before: new Date() }}
                    className="rounded-sm"
                    data-testid="block-range-start-calendar"
                  />
                </div>
                {blockRangeStart && (
                  <p className="text-green-500 text-xs text-center font-mono">
                    {format(blockRangeStart, 'EEE, MMM d, yyyy')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-xs uppercase tracking-wider">Last day off</Label>
                <div className="flex justify-center border border-zinc-800 rounded-sm p-2">
                  <Calendar
                    mode="single"
                    selected={blockRangeEnd}
                    onSelect={setBlockRangeEnd}
                    disabled={{ before: blockRangeStart || new Date() }}
                    className="rounded-sm"
                    data-testid="block-range-end-calendar"
                  />
                </div>
                {blockRangeEnd && (
                  <p className="text-green-500 text-xs text-center font-mono">
                    {format(blockRangeEnd, 'EEE, MMM d, yyyy')}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Reason (Optional)</Label>
              <Input
                value={blockRangeReason}
                onChange={(e) => setBlockRangeReason(e.target.value)}
                className="bg-zinc-950 border-zinc-800 font-mono"
                placeholder="e.g., Sick, Holiday, Family time"
                data-testid="block-range-reason-input"
              />
            </div>
            {blockRangeStart && blockRangeEnd && blockRangeEnd >= blockRangeStart && (
              <div className="bg-green-900/20 border border-green-800 rounded-sm p-3 text-center">
                <p className="text-green-400 text-sm">
                  Blocking <span className="font-bold">{Math.round((blockRangeEnd - blockRangeStart) / 86400000) + 1}</span> day(s)
                </p>
              </div>
            )}
            <Button
              onClick={handleBlockDateRange}
              disabled={blockRangeSaving || !blockRangeStart || !blockRangeEnd}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50"
              data-testid="confirm-block-range-btn"
            >
              {blockRangeSaving ? 'Blocking…' : 'Block These Days'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">Add Admin User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Username</Label>
              <Input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono" placeholder="newadmin" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono" />
            </div>
            <Button onClick={handleAddUser} className="w-full bg-green-600 hover:bg-green-700">Create User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Current Password</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">New Password</Label>
              <Input type="password" value={newPasswordChange} onChange={(e) => setNewPasswordChange(e.target.value)} className="bg-zinc-950 border-zinc-800 font-mono" />
            </div>
            <Button onClick={handleChangePassword} className="w-full bg-green-600 hover:bg-green-700">Change Password</Button>
          </div>
        </DialogContent>
      </Dialog>
      <EditBookingDialog
        open={!!editingBooking}
        onOpenChange={(v) => { if (!v) setEditingBooking(null); }}
        booking={editingBooking}
        onSaved={handleBookingSaved}
      />
      <InstallPWA />
    </div>
  );
}

function BookingCard({ booking, onCancel, onApprove, onDeny, onEdit }) {
  const status = booking.status || 'pending';
  const statusStyles = {
    pending: 'bg-yellow-900/30 border-yellow-700 text-yellow-300',
    confirmed: 'bg-green-900/30 border-green-800 text-green-500',
    denied: 'bg-red-900/30 border-red-800 text-red-400',
    cancelled: 'bg-zinc-800 border-zinc-700 text-zinc-400',
  };
  const c = booking.customer || {};
  const isGoogleImport = booking.source === 'google_import';
  // Placeholder = still untouched since GCal import (name begins with 📅).
  const needsFillIn = isGoogleImport && typeof c.name === 'string' && c.name.startsWith('📅');
  return (
    <div
      className={`glass p-4 border bg-zinc-900/50 hover:border-zinc-700 transition-colors ${
        status === 'pending' ? 'border-yellow-800/60' : needsFillIn ? 'border-blue-800/60' : 'border-zinc-800'
      }`}
      data-testid={`booking-card-${booking.id}`}
    >
      <div className="flex justify-between items-start mb-3 gap-2">
        <div>
          <p className="font-['JetBrains_Mono'] text-green-500 font-medium">
            {format(parseISO(booking.date + 'T00:00:00'), 'EEE, MMM d')}
          </p>
          <p className="text-white text-sm">{booking.slot_label}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-1 border text-xs uppercase tracking-wider ${statusStyles[status] || statusStyles.pending}`}>
            {status}
          </span>
          {isGoogleImport && (
            <span className="px-2 py-0.5 bg-blue-900/40 border border-blue-700 text-blue-300 text-[10px] uppercase tracking-wider">
              From Google
            </span>
          )}
        </div>
      </div>
      <div className="space-y-2 text-sm border-t border-zinc-800 pt-3">
        <p className="text-zinc-300 font-medium">{c.name}</p>
        <div className="flex items-center gap-2 text-zinc-400">
          <Phone className="w-3 h-3" />
          <a href={`tel:${c.phone}`} className="hover:text-white">{c.phone}</a>
        </div>
        <div className="flex items-center gap-2 text-zinc-400">
          <Mail className="w-3 h-3" />
          <a href={`mailto:${c.email}`} className="hover:text-white truncate">{c.email}</a>
        </div>
        <div className="flex items-center gap-2 text-zinc-400">
          <Car className="w-3 h-3" />
          <span>{c.vehicle_year} {c.vehicle_make} {c.vehicle_model}</span>
        </div>
        {(c.fuel_system || c.aspiration) && (
          <div className="text-xs text-zinc-500 pt-1 border-t border-zinc-800 mt-2 space-y-0.5">
            {c.fuel_system && (
              <p>Fuel: <span className="text-zinc-300">{c.fuel_system}{c.ecu_type ? ` — ${c.ecu_type}` : ''}{c.carb_type ? ` — ${c.carb_type}` : ''}</span></p>
            )}
            {c.aspiration && <p>Aspiration: <span className="text-zinc-300">{c.aspiration}</span></p>}
            {c.axle_type && <p>Axle: <span className="text-zinc-300">{c.axle_type}</span></p>}
            {c.stud_pattern && <p>Studs: <span className="text-zinc-300">{c.stud_pattern}</span></p>}
            {c.known_faults && <p className="text-red-400">Faults: <span>{c.known_faults}</span></p>}
          </div>
        )}
      </div>

      {status === 'pending' && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button
            size="sm"
            onClick={() => onApprove(booking.id)}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid={`approve-${booking.id}`}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDeny(booking.id)}
            className="border-red-800 text-red-400 hover:bg-red-900/30 hover:text-red-300"
            data-testid={`deny-${booking.id}`}
          >
            Deny
          </Button>
        </div>
      )}
      {status === 'confirmed' && (
        <div className="flex flex-col gap-2 mt-3">
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(booking)}
              className={`${needsFillIn ? 'border-blue-700 text-blue-300 hover:bg-blue-900/30' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}
              data-testid={`edit-${booking.id}`}
            >
              <Edit className="w-4 h-4 mr-2" />
              {needsFillIn ? 'Fill in customer details' : 'Edit customer details'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCancel(booking.id)}
            className="w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
            data-testid={`cancel-${booking.id}`}
          >
            <X className="w-4 h-4 mr-2" />Cancel Booking
          </Button>
        </div>
      )}
    </div>
  );
}

function CancelledBookingCard({ booking }) {
  const isDenied = booking.status === 'denied';
  return (
    <div className="glass p-4 border border-zinc-800 bg-zinc-900/30" data-testid={`cancelled-booking-${booking.id}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="font-['JetBrains_Mono'] text-zinc-500">{format(parseISO(booking.date + 'T00:00:00'), 'EEE, MMM d')}</p>
          <p className="text-zinc-600 text-sm">{booking.slot_label}</p>
        </div>
        <span className={`px-2 py-1 border text-xs uppercase tracking-wider ${
          isDenied ? 'bg-red-900/30 border-red-800 text-red-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400'
        }`}>{isDenied ? 'Denied' : 'Cancelled'}</span>
      </div>
      <p className="text-zinc-500 mt-2">{booking.customer.name}</p>
      {isDenied && booking.denial_reason && (
        <p className="text-red-400/70 text-xs mt-1 italic">Reason: {booking.denial_reason}</p>
      )}
    </div>
  );
}

function BlockedSlotCard({ blocked, onUnblock }) {
  const slotLabel = ALL_TIME_SLOTS.find((s) => s.id === blocked.slot_id)?.label || 'Full Day';
  return (
    <div className="glass p-4 border border-zinc-800 bg-zinc-900/50">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-['JetBrains_Mono'] text-green-500 font-medium">
            {format(parseISO(blocked.date + 'T00:00:00'), 'EEE, MMM d, yyyy')}
          </p>
          <p className="text-white text-sm">{blocked.slot_id ? slotLabel : 'Full Day'}</p>
          {blocked.reason && <p className="text-zinc-500 text-xs mt-1">{blocked.reason}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onUnblock(blocked.id)} className="text-zinc-400 hover:text-white">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function UserCard({ user, currentUser, onDelete }) {
  return (
    <div className="glass p-4 border border-zinc-800 bg-zinc-900/50">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
            <Users className="w-5 h-5 text-zinc-400" />
          </div>
          <div>
            <p className="text-white font-medium">{user.username}</p>
            <p className="text-zinc-500 text-xs">
              {user.username === currentUser && '(You) '}
              Created {format(parseISO(user.created_at), 'MMM d, yyyy')}
            </p>
          </div>
        </div>
        {user.username !== currentUser && (
          <Button variant="ghost" size="sm" onClick={() => onDelete(user.username)} className="text-red-400 hover:text-red-300">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
